import React from "react";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import LoadingWrapper from "components/Shared/LoadingWrapper";
import RichTextEditor from "components/Shared/RichTextEditor";
import { BaseTextField } from "components/FormBuilder/fields/BaseTextField";
import BaseSelect from "components/Shared/Base/BaseSelect";
import FileDropzone from "components/Shared/FileDropzone";

import { useDOUploadFile } from "hooks/useDoSpaceUpload";
import { useAuth } from "hooks";


const INITIAL_STATE = {
  title: "",
  content: "",
  visibleTo: [],
  attachments: [],
  canReply: true,
};

const visibleOptions = ["Customer", "Vendor", "Contractor", "VendorStaff"];

const AnnouncementForm = ({
  announcementDetail = null,
  isBusy = false,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { viewEntity } = useAuth();

  const { handleUploadFiles, uploadingFiles } = useDOUploadFile();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    defaultValues: INITIAL_STATE,
  });

  // File handling logic
  const handleOnAnnouncementSubmit = async (data) => {
    const [existingAttachments, newAttachments] = data?.attachments?.reduce(
      ([existing, fresh], file) =>
        file?.publicId || file?.url
          ? [[...existing, file], fresh]
          : [existing, [...fresh, file]],
      [[], []]
    );

    let uploadedFiles = [];

    if (newAttachments.length) {
      const res = await handleUploadFiles(newAttachments, {
        folderName: "ANNOUNCEMENT",
        entity: viewEntity?.entity?._id,
        account: viewEntity?.entity?.account?._id,
      });
      uploadedFiles = res?.uploadedFiles || [];
    }

    const finalAttachments = [...existingAttachments, ...uploadedFiles];

    onSubmit({
      ...data,
      attachments: finalAttachments,
    });
  };

  React.useEffect(() => {
    if (!announcementDetail) return;
    reset(announcementDetail);
  }, [announcementDetail, reset]);

  return (
    <LoadingWrapper loading={isBusy || uploadingFiles}>
      <Box padding={3}>
        <Grid container spacing={5}>
          <Grid item xs={6}>
            <Controller
              name="title"
              control={control}
              rules={{ required: "Title is required" }}
              render={({ field }) => (
                <BaseTextField
                  required
                  fullWidth
                  label="Title"
                  type="text"
                  size="small"
                  {...field}
                  error={!!errors?.title}
                  helperText={errors?.title?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="visibleTo"
              control={control}
              rules={{ required: "Visible To is required" }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  fullWidth
                  required
                  label="Sent To"
                  options={visibleOptions}
                  multiple
                  error={!!errors?.visibleTo}
                  helperText={errors?.visibleTo?.message}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="canReply"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch color="primary" {...field} checked={field.value} />
                  }
                  label="Can Reply ?"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <div
              style={{ color: "gray", fontSize: 15, marginBottom: ".75rem" }}
            >
              Content*
            </div>
            <Controller
              name="content"
              control={control}
              rules={{ required: "Content is required" }}
              render={({ field: { onChange } }) => (
                <RichTextEditor
                  initialValue={
                    announcementDetail?.content
                      ? announcementDetail.content
                      : "<p>Enter Announcement Content.</p>"
                  }
                  onChange={onChange}
                  error={!!errors?.content}
                  helperText={errors?.content?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <div
              style={{
                color: "gray",
                fontSize: 15,
                marginBottom: ".75rem",
              }}
            >
              Attachments
            </div>
            <Controller
              name="attachments"
              control={control}
              render={({ field }) => (
                <FileDropzone
                  multiple
                  maxFiles={5}
                  files={field?.value ?? []}
                  onFileChange={(files) => {
                    setValue("attachments", files);
                    field.onChange(files);
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider />

      <Box
        paddingX={3}
        paddingY={2}
        gap={2}
        style={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button disabled={isBusy} onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isBusy}
          onClick={handleSubmit(handleOnAnnouncementSubmit)}
        >
          {announcementDetail ? "Update" : "Add"}
        </Button>
      </Box>
    </LoadingWrapper>
  );
};

export default AnnouncementForm;
